import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useUser } from "@/app/session.tsx";
import { TopBar } from "@/components/TopBar.tsx";
import { Button } from "@/components/primitives.tsx";
import { api } from "@/lib/api.ts";

/** The landing page: two modules, and a way back into recent work. */
export function HomeRoute() {
  const { t } = useTranslation();
  const user = useUser();
  const navigate = useNavigate();

  // "Notebook baru" OPENS an empty studio; the notebook itself is created the moment
  // sources are added, which is the first point at which it can be given a meaningful
  // name. Creating it here left an untitled row behind for every click that went nowhere.
  const createNotebook = useMutation({
    mutationFn: async () => undefined,
    onSuccess: () => {
      void navigate({ to: "/studio" });
    },
  });

  const conversations = useQuery({
    queryKey: ["conversations", user.subject],
    queryFn: () => api.conversations(user.subject),
  });

  return (
    <div className="lib">
      <TopBar />
      <div className="wrap">
        <h1 className="page-title">{t("home.title")}</h1>
        <p className="page-sub">{t("home.subtitle")}</p>

        <div className="row" style={{ margin: "var(--s5) 0 var(--s3)" }}>
          <div className="eyebrow">{t("home.notebooks")}</div>
          <span className="spacer" />
          <Button
            variant="primary"
            disabled={createNotebook.isPending}
            onClick={() => createNotebook.mutate()}
          >
            {t("home.newNotebook")}
          </Button>
        </div>

        <div className="notebook-grid">
          {conversations.data && conversations.data.length > 0 ? (
            conversations.data.map((conversation) => (
              <Link
                key={conversation.id}
                to="/studio/$conversationId"
                params={{ conversationId: conversation.id }}
                className="notebook-card"
              >
                <span className="notebook-card-title">
                  {conversation.title ?? t("home.untitled")}
                </span>
              </Link>
            ))
          ) : (
            <p className="hint">{t("home.noNotebooks")}</p>
          )}
        </div>

        <div className="divider" />
        <button
          type="button"
          className="launch launch-quiet"
          onClick={() => void navigate({ to: "/collections" })}
        >
          <span className="launch-title">{t("home.collectionsTitle")}</span>
          <span className="launch-sub">{t("home.collectionsSub")}</span>
        </button>
      </div>
    </div>
  );
}
